import {useEffect, useRef, useState} from 'react';

import type {AllAssignmentsType, GranularTaskAssignmentsType} from './types';
import type {WriteTaskTemplateSerializedType} from 'sweet-types';

// all task assignment fields
export type AllInitialAssignmentFieldsType = Pick<
  WriteTaskTemplateSerializedType,
  'user_ids' | 'role_ids' | 'team_ids' | 'assignment_ids'
>;

/**
 * Hook which receives a list of assigned IDs (globally and by steps),
 * then read redux data to convert the list items to proper assignees representations
 * as BasicUserSerialized, RoleSerialized, etc.
 * It relies on the users/roles/teams fetch by the select components through the fetchids prop
 */
export function useAssignments(initial: AllAssignmentsType): {
  assignments: AllAssignmentsType;
  initialGranular: GranularTaskAssignmentsType;
  setAssignments: (
    assignments:
      | null
      | AllAssignmentsType
      | ((assignments: AllAssignmentsType) => AllAssignmentsType)
  ) => void;
} {
  const [assignments, setAssignments] = useState<AllAssignmentsType>(initial);
  const initialGranular = useRef(initial.granular);

  // update assignments after fetching task instance data
  useEffectAfterMounted(() => {
    setAssignments(initial);
    initialGranular.current = initial.granular;
  }, [initial]);

  return {
    assignments,
    initialGranular: initialGranular.current,
    setAssignments: (assignments) =>
      setAssignments(
        assignments == null ? INITIAL_ASSIGNMENTS_STATE : assignments
      ),
  };
}

export const INITIAL_ASSIGNMENTS_STATE: AllAssignmentsType = {
  global: {
    users: [],
    roles: [],
    teams: [],
  },
  granular: [],
};

export function useEffectAfterMounted(fn: () => unknown, deps: Array<unknown>) {
  const mounted = useRef(false);

  useEffect(() => {
    if (mounted.current) {
      fn();
    }

    mounted.current = true;
  }, deps);
}
